// Libs
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
// Context
import { ModalBuilderContext } from 'core/context';
// Hooks
import usePaymentApi from 'core/hooks/usePaymentApi';
import { useToastMessage } from 'hooks';
// Selectors
import { selectProfile } from 'screens/Login/selectors';
// Components
import { Button, LogoIcon, Modal, Select, Span, Wrapper } from 'components';
// Utils
import { filterOption, getSpecificUsers } from 'utils/libs';
import GENERAL from 'utils/constants/general';

const { Option } = Select;
const { DIVISION_ID } = GENERAL;

const EMPTY_FORM = {
	division_id: undefined,
	paid_to: undefined,
};

const CreatePayment = () => {
	const { payload: modalPayload, cleanModal } = useContext(ModalBuilderContext);
	const { paycutId, refreshPayments } = modalPayload;

	const profile = useSelector(selectProfile);

	const api = usePaymentApi();
	const sendToast = useToastMessage();

	const [allUsers, setAllUsers] = useState([]);
	const [isFetching, setIsFetching] = useState(true);
	const [form, _setForm] = useState(EMPTY_FORM);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const setForm = values => _setForm(cur => ({ ...cur, ...values }));

	useEffect(() => {
		api
			.getOrganizationUsers()
			.then(data => {
				setAllUsers(Array.isArray(data) ? data : []);
				setIsFetching(false);
			})
			.catch(err => {
				sendToast({ message: err.message, type: 'error' });
				setIsFetching(false);
			});
	}, []);

	const allReceivers = getSpecificUsers(allUsers, {
		entities: true,
		experts: true,
	});

	const filteredReceivers = allReceivers.filter(
		receiver =>
			receiver.division_id === form.division_id &&
			receiver.id !== profile?.entity?.id,
	);

	const handleDivisionChange = value => {
		setForm({ division_id: value, paid_to: undefined });
	};

	const handleSubmit = () => {
		if (!form.division_id) {
			sendToast({ message: 'Selecciona una división', type: 'warning' });
			return;
		}
		if (!form.paid_to) {
			sendToast({ message: 'Selecciona un destinatario', type: 'warning' });
			return;
		}

		setIsSubmitting(true);
		api
			.createPayment({
				paycut_id: paycutId,
				division_id: form.division_id,
				paid_to: form.paid_to,
				type: 'payment',
			})
			.then(response => {
				const paymentId = Array.isArray(response) ? response[0] : response;
				sendToast({
					message: `Pago creado: [${paymentId}]`,
					type: 'success',
				});
				if (refreshPayments) refreshPayments();
				cleanModal();
			})
			.catch(err => {
				sendToast({ message: err.message || 'Error al crear el pago', type: 'error' });
				setIsSubmitting(false);
			});
	};

	return (
		<Modal
			title={null}
			closable={false}
			centered={false}
			visible={true}
			onCancel={cleanModal}
			footer={null}
		>
			<Wrapper
				highBackground
				width='100%'
				padding='10px'
				margin='0'
				flexDirection='column'
				alignItems='center'
				justifyContent='flex-start'
			>
				<Wrapper width='95%' height='28px' justifyContent='flex-start'>
					<Span fontSize='xl'>Crear Pago</Span>
				</Wrapper>

				{isFetching ? (
					<LogoIcon spin />
				) : null}
				<Wrapper padding='10px 0' width='95%' flexDirection='column' alignItems='stretch' style={{ display: isFetching ? 'none' : 'flex' }}>
					<Select
						showSearch
						placeholder='División*'
						value={form.division_id}
						filterOption={filterOption}
						onChange={handleDivisionChange}
						style={{ marginBottom: 12 }}
					>
						<Option value={DIVISION_ID.ENTITY}>
							<em>Entidad</em>
						</Option>
						<Option value={DIVISION_ID.EXP}>
							<em>Técnico</em>
						</Option>
					</Select>

					<Select
						showSearch
						placeholder='Destinatario*'
						value={form.paid_to}
						filterOption={filterOption}
						onChange={value => setForm({ paid_to: value })}
						style={{ marginBottom: 12 }}
						disabled={!form.division_id}
					>
						{filteredReceivers.map(receiver => (
							<Option key={receiver.id} value={receiver.id}>
								<em>{receiver.name}</em>
							</Option>
						))}
					</Select>

					<Wrapper justifyContent='flex-end' padding='0'>
						{isSubmitting ? (
							<LogoIcon button spin />
						) : (
							<Button
								mode='primary'
								disabled={!form.division_id || !form.paid_to}
								onClick={handleSubmit}
							>
								Crear
							</Button>
						)}
					</Wrapper>
				</Wrapper>
			</Wrapper>
		</Modal>
	);
};

export default CreatePayment;
